import { AxiosInstance } from 'axios';
import axios from 'axios';

export abstract class CrudService<T> {
    protected apiClient: AxiosInstance;
    protected endpoint: string;

    constructor(apiClient: AxiosInstance, endpoint: string) {
        this.apiClient = apiClient;
        this.endpoint = endpoint;
    }

    private getErrorMessage(error: unknown): string {
        if (axios.isAxiosError(error)) {
            const responseData = error.response?.data;

            if (typeof responseData === 'string' && responseData.trim().length > 0) {
                return responseData;
            }

            if (responseData && typeof responseData === 'object' && 'error' in responseData) {
                const apiError = (responseData as { error?: unknown }).error;
                if (typeof apiError === 'string' && apiError.trim().length > 0) {
                    return apiError;
                }
            }

            if (responseData && typeof responseData === 'object' && 'message' in responseData) {
                const apiMessage = (responseData as { message?: unknown }).message;
                if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
                    return apiMessage;
                }
            }
        }

        if (error instanceof Error && error.message.trim().length > 0) {
            return error.message;
        }

        return 'An error occurred';
    }

    async getAll<R = T[]>(): Promise<R> {
        try {
            const response = await this.apiClient.get(this.endpoint);
            return response.data as R;
        } catch (error) {
            throw new Error(this.getErrorMessage(error));
        }
    }

    async get(id: string): Promise<T> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}`);
            return response.data;
        } catch (error) {
            throw new Error(this.getErrorMessage(error));
        }
    }

    async create(item: T): Promise<T> {
        try {
            const response = await this.apiClient.post(this.endpoint, item);
            return response.data;
        } catch (error) {
            throw new Error(this.getErrorMessage(error));
        }
    }

    async update(id: string, item: T): Promise<T> {
        try {
            const response = await this.apiClient.put(`${this.endpoint}/${id}`, item);
            return response.data;
        } catch (error) {
            throw new Error(this.getErrorMessage(error));
        }
    }

    async delete(id: string): Promise<void> {
        try {
            await this.apiClient.delete(`${this.endpoint}/${id}`);
        } catch (error) {
            throw new Error(this.getErrorMessage(error));
        }
    }
}